import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { softDeleteWithUndo } from "@/lib/softDelete";
import { Star, Edit3, Loader2, Copy, Trash2, FileUp, Briefcase } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

/**
 * Grid of created CV variants. Clicking a card opens it; clicking the star
 * marks it as the user's Central / Default CV (persisted via /auth/preferences).
 */
function Thumb({ variantId, testid }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancel = false;
    let blobUrl = null;
    api.get(`/resumes/${variantId}/thumbnail.png`, { responseType: "blob" })
      .then(({ data }) => {
        if (cancel) return;
        blobUrl = URL.createObjectURL(data);
        setUrl(blobUrl);
      })
      .catch(() => { /* leave placeholder */ });
    return () => { cancel = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [variantId]);
  if (!url) return null;
  return (
    <img data-testid={testid} src={url} alt="" className="w-full h-full object-cover" style={{ objectPosition: "top" }} />
  );
}

export default function CVOverviewList({ onOpenVariant }) {
  const confirm = useConfirm();
  const { t } = useT();
  const [variants, setVariants] = useState([]);
  const [centralId, setCentralId] = useState("");
  const [uploadedCv, setUploadedCv] = useState(null); // { filename, size, updated_at } | null
  const [uploadedIsDefault, setUploadedIsDefault] = useState(false);
  const [loading, setLoading] = useState(true);
  const [starBusy, setStarBusy] = useState(null);
  const [dupBusy, setDupBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: me }, { data: list }, { data: cv }] = await Promise.all([
        api.get("/auth/me"),
        api.get("/resumes").catch(() => ({ data: [] })),
        api.get("/cv").catch(() => ({ data: { present: false } })),
      ]);
      setCentralId(me?.central_resume_id || "");
      setUploadedIsDefault(!!me?.central_uploaded_cv);
      setVariants(Array.isArray(list) ? list : []);
      setUploadedCv(cv?.present ? cv : null);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setDefault = async (rid) => {
    const newVal = centralId === rid ? "" : rid;
    setStarBusy(rid);
    try {
      await api.patch("/auth/preferences", { central_resume_id: newVal });
      setCentralId(newVal);
      // Setting a variant as default clears the uploaded-CV default (mutual exclusion).
      if (newVal) setUploadedIsDefault(false);
      toast.success(newVal ? t("cvOvSetDefault") : t("cvOvUnsetDefault"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("cvCentralSaveFailed"));
    } finally {
      setStarBusy(null);
    }
  };

  const setUploadedDefault = async () => {
    const newVal = !uploadedIsDefault;
    setStarBusy("__uploaded__");
    try {
      await api.patch("/auth/preferences", { central_uploaded_cv: newVal });
      setUploadedIsDefault(newVal);
      if (newVal) setCentralId("");
      toast.success(newVal ? t("cvOvSetDefault") : t("cvOvUnsetDefault"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("cvCentralSaveFailed"));
    } finally {
      setStarBusy(null);
    }
  };

  const removeUploaded = async () => {
    if (!(await confirm(t("cvOvUploadedConfirmDelete")))) return;
    try {
      await api.delete("/cv");
      setUploadedCv(null);
      setUploadedIsDefault(false);
      toast.success(t("cvOvUploadedDeleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dashDeleteFailed"));
    }
  };

  const duplicate = async (rid) => {
    setDupBusy(rid);
    try {
      const { data } = await api.post(`/resumes/${rid}/duplicate`);
      toast.success(t("rbDuplicated"));
      // Insert new variant right after the source and bump the source counter
      setVariants((prev) => {
        const i = prev.findIndex((x) => x.id === rid);
        const inserted = {
          id: data.id,
          name: data.name,
          updated_at: data.updated_at,
          is_default: false,
          duplicated_from: data.duplicated_from || rid,
        };
        const next = prev.map((v) =>
          v.id === rid ? { ...v, duplicate_count: (v.duplicate_count || 0) + 1 } : v
        );
        if (i >= 0) next.splice(i + 1, 0, inserted); else next.unshift(inserted);
        return next;
      });
      onOpenVariant?.(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("rbSaveFailed"));
    } finally {
      setDupBusy(null);
    }
  };

  const remove = async (rid) => {
    // 1. Ask the backend how many letters still reference this CV
    let usage = { letters_count: 0, letters: [] };
    try {
      const { data } = await api.get(`/resumes/${rid}/usage`);
      usage = data || usage;
    } catch { /* usage is best-effort */ }

    // 2. Build a context-aware confirm dialog
    let msg = t("rbConfirmDelete");
    if (usage.letters_count > 0) {
      const titles = usage.letters
        .slice(0, 5)
        .map((l) => `• ${l.job_title || "?"} — ${l.company || "?"}`)
        .join("\n");
      const more = usage.letters_count > 5 ? `\n… +${usage.letters_count - 5}` : "";
      msg = `${t("rbConfirmDeleteWithLetters", { count: usage.letters_count })}\n\n${titles}${more}\n\n${t("rbConfirmDeleteLettersHint")}`;
    }
    if (!(await confirm(msg))) return;

    // 3. Optimistic hide + Undo toast (30s window before we hit DELETE)
    const removedIndex = variants.findIndex((v) => v.id === rid);
    const removedItem = variants[removedIndex];
    const wasCentral = centralId === rid;
    if (!removedItem) return;

    softDeleteWithUndo({
      kind: "resume",
      id: rid,
      apiDelete: () => api.delete(`/resumes/${rid}`),
      optimisticHide: () => {
        setVariants((prev) => prev.filter((v) => v.id !== rid));
        if (wasCentral) setCentralId("");
      },
      restore: () => {
        setVariants((prev) => {
          if (prev.some((v) => v.id === rid)) return prev;
          const next = [...prev];
          next.splice(Math.min(removedIndex, next.length), 0, removedItem);
          return next;
        });
        if (wasCentral) setCentralId(rid);
      },
      labels: {
        success: t("rbDeleted"),
        undone: t("rbUndone"),
        undo: t("undo"),
        error: t("dashDeleteFailed"),
      },
    });
  };

  if (loading) return <p className="font-mono text-xs text-neutral-500">{t("loading")}</p>;

  if (variants.length === 0 && !uploadedCv) {
    return (
      <div className="ed-card p-10 text-center" data-testid="cv-overview-empty">
        <p className="font-serif text-lg text-neutral-500 mb-3">{t("cvOvEmpty")}</p>
        <p className="text-xs text-neutral-500 font-mono">{t("cvOvEmptyHint")}</p>
      </div>
    );
  }

  return (
    <div className="ed-card overflow-x-auto" data-testid="cv-overview-list">
      <table className="ed-table">
        <thead>
          <tr>
            <th style={{ width: 60 }}>&nbsp;</th>
            <th>{t("cvOvColName")}</th>
            <th>{t("cvOvColUpdated")}</th>
            <th className="text-right">{t("dashColActions")}</th>
          </tr>
        </thead>
        <tbody>
          {uploadedCv && (
            <tr data-testid="cv-overview-uploaded-row" className="bg-neutral-50/40">
              <td>
                <div
                  className="w-10 h-14 border border-neutral-300 bg-white flex items-center justify-center shrink-0"
                  title={t("cvOvUploadedTitle")}
                  data-testid="cv-overview-uploaded-icon"
                >
                  <FileUp size={18} strokeWidth={1.5} className="text-neutral-500" />
                </div>
              </td>
              <td className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-serif text-lg leading-tight truncate max-w-[24rem]" data-testid="cv-overview-uploaded-name">
                    {uploadedCv.filename || t("cvOvUploadedFallbackName")}
                  </p>
                  <span
                    className="font-mono text-[0.55rem] uppercase tracking-[0.15em] bg-neutral-900 text-[#F9F8F6] px-2 py-0.5"
                    data-testid="cv-overview-uploaded-badge"
                  >
                    {t("cvOvUploadedBadge")}
                  </span>
                  {uploadedIsDefault && (
                    <span
                      className="font-mono text-[0.55rem] uppercase tracking-[0.15em] bg-amber-300 text-neutral-900 px-2 py-0.5"
                      data-testid="cv-overview-uploaded-default-badge"
                    >
                      {t("cvOvDefault")}
                    </span>
                  )}
                </div>
                <p className="text-xs text-neutral-500 mt-1 italic font-serif">
                  {t("cvOvUploadedHint")}
                </p>
              </td>
              <td className="text-sm font-mono text-neutral-700 whitespace-nowrap">
                {uploadedCv.updated_at ? new Date(uploadedCv.updated_at).toLocaleDateString() : "—"}
              </td>
              <td className="text-right">
                <div className="flex justify-end gap-2">
                  <button
                    data-testid="cv-overview-uploaded-star"
                    onClick={setUploadedDefault}
                    disabled={starBusy === "__uploaded__"}
                    title={uploadedIsDefault ? t("cvOvUnsetDefaultCta") : t("cvOvSetDefaultCta")}
                    className={`p-2 border transition-colors ${uploadedIsDefault ? "border-amber-500 text-amber-700" : "border-neutral-300 hover:border-amber-500 hover:text-amber-600"}`}
                  >
                    {starBusy === "__uploaded__"
                      ? <Loader2 size={13} className="animate-spin" />
                      : <Star size={13} strokeWidth={1.7} fill={uploadedIsDefault ? "currentColor" : "none"} />}
                  </button>
                  <button
                    data-testid="cv-overview-uploaded-delete"
                    onClick={removeUploaded}
                    title={t("rbConfirmDelete")}
                    className="p-2 border border-neutral-300 text-neutral-500 hover:border-rose-700 hover:text-rose-700 transition-colors"
                  >
                    <Trash2 size={13} strokeWidth={1.7} />
                  </button>
                </div>
              </td>
            </tr>
          )}
          {variants.map((v, idx) => {
            const isDefault = v.id === centralId;
            return (
              <tr
                key={v.id}
                data-testid={`cv-overview-card-${idx}`}
                className="cursor-pointer"
                onClick={() => onOpenVariant?.(v.id)}
              >
                <td>
                  <div
                    className="w-10 h-14 bg-neutral-100 border border-neutral-200 overflow-hidden shrink-0"
                    data-testid={`cv-overview-thumb-${idx}`}
                  >
                    <Thumb variantId={v.id} testid={`cv-overview-thumb-img-${idx}`} />
                  </div>
                </td>
                <td className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {(() => {
                      // New two-tier heading — the function title (from
                      // personal.title) reads FIRST as the primary
                      // identifier, with the (often duplicated) CV base
                      // name shown small underneath. This solves the
                      // "all CVs look the same" problem the user
                      // reported when many forks share a base name.
                      const funcTitle = ((v.personal || {}).title || "").trim();
                      const rawName = (v.name || "").trim();
                      // Strip the redundant " — <suffix>" the fork
                      // helper appends so the subtitle stays tidy.
                      const nameForSub = rawName.split(" — ")[0] || rawName;
                      return funcTitle ? (
                        <p
                          className="font-serif text-lg leading-tight truncate max-w-[24rem]"
                          data-testid={`cv-overview-func-title-${idx}`}
                        >
                          {funcTitle}
                        </p>
                      ) : (
                        <p className="font-serif text-lg leading-tight truncate max-w-[24rem]">
                          {rawName || t("cvCentralUnnamed")}
                        </p>
                      );
                    })()}
                    {isDefault && (
                      <span
                        className="font-mono text-[0.55rem] uppercase tracking-[0.15em] bg-amber-300 text-neutral-900 px-2 py-0.5"
                        data-testid={`cv-overview-default-badge-${idx}`}
                      >
                        {t("cvOvDefault")}
                      </span>
                    )}
                    {!isDefault && v.duplicated_from && (
                      <span
                        className="font-mono text-[0.55rem] uppercase tracking-[0.15em] bg-neutral-900 text-[#F9F8F6] px-2 py-0.5"
                        data-testid={`cv-overview-copy-badge-${idx}`}
                        title={t("cvOvCopyBadgeTitle")}
                      >
                        {t("cvOvCopyBadge")}
                      </span>
                    )}
                    {v.duplicate_count > 0 && (
                      <span
                        className="font-mono text-[0.55rem] uppercase tracking-[0.15em] border border-neutral-300 text-neutral-700 px-2 py-0.5 inline-flex items-center gap-1"
                        data-testid={`cv-overview-dup-count-${idx}`}
                        title={t("cvOvDupCountTitle", { count: v.duplicate_count })}
                      >
                        <Copy size={9} strokeWidth={1.7} /> ×{v.duplicate_count}
                      </span>
                    )}
                  </div>
                  {/* Subtitle line — CV base name shown small so
                      duplicates are still distinguishable, plus the
                      click-to-edit hint. Uses the stripped base name
                      to avoid re-showing the vacancy suffix. */}
                  <p
                    className="text-xs text-neutral-500 mt-1 flex items-center gap-2 flex-wrap"
                    data-testid={`cv-overview-subtitle-${idx}`}
                  >
                    {(() => {
                      // Vacature-badge — AI-forks carry `generated_from_job.job_title`.
                      // Rendered as a distinct outlined badge so users spot at a
                      // glance which vacancy each variant was tailored for, even
                      // when the CV's own personal.title was later edited.
                      const jobTitle = (v.generated_from_job?.job_title || "").trim();
                      if (!jobTitle) return null;
                      return (
                        <span
                          className="font-mono text-[0.55rem] uppercase tracking-[0.12em] border border-neutral-300 text-neutral-700 px-2 py-0.5 inline-flex items-center gap-1 max-w-[22rem] truncate"
                          data-testid={`cv-overview-vacancy-badge-${idx}`}
                          title={t("cvOvVacancyBadgeTitle", { title: jobTitle })}
                        >
                          <Briefcase size={9} strokeWidth={1.7} /> <span className="truncate">{jobTitle}</span>
                        </span>
                      );
                    })()}
                    {(v.personal || {}).title && (v.name || "").trim() && (
                      <span className="font-mono uppercase tracking-[0.1em] truncate max-w-[22rem]">
                        {(v.name || "").split(" — ")[0]}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-neutral-500">
                      <Edit3 size={11} strokeWidth={1.5} /> {t("cvOvClickToEdit")}
                    </span>
                  </p>
                </td>
                <td className="text-sm font-mono text-neutral-700 whitespace-nowrap">
                  {v.updated_at ? new Date(v.updated_at).toLocaleDateString() : "—"}
                </td>
                <td className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-2">
                    <button
                      data-testid={`cv-overview-star-${idx}`}
                      onClick={() => setDefault(v.id)}
                      disabled={starBusy === v.id}
                      title={isDefault ? t("cvOvUnsetDefaultCta") : t("cvOvSetDefaultCta")}
                      className={`p-2 border transition-colors ${isDefault ? "border-amber-500 text-amber-700" : "border-neutral-300 hover:border-amber-500 hover:text-amber-600"}`}
                    >
                      {starBusy === v.id
                        ? <Loader2 size={13} className="animate-spin" />
                        : <Star size={13} strokeWidth={1.7} fill={isDefault ? "currentColor" : "none"} />}
                    </button>
                    <button
                      data-testid={`cv-overview-duplicate-${idx}`}
                      onClick={() => duplicate(v.id)}
                      disabled={dupBusy === v.id}
                      title={t("rbDuplicate")}
                      className="p-2 border border-neutral-300 hover:border-neutral-900 transition-colors"
                    >
                      {dupBusy === v.id
                        ? <Loader2 size={13} className="animate-spin" />
                        : <Copy size={13} strokeWidth={1.7} />}
                    </button>
                    <button
                      data-testid={`cv-overview-delete-${idx}`}
                      onClick={() => remove(v.id)}
                      title={t("rbConfirmDelete")}
                      className="p-2 border border-neutral-300 text-neutral-500 hover:border-rose-700 hover:text-rose-700 transition-colors"
                    >
                      <Trash2 size={13} strokeWidth={1.7} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
